// Carries a tenant's advanced SSO config (their own Entra app) across the redirect from the tenant
// app to the shared id app. The id app has no per-tenant config, so cold-login and logout redirects
// must bundle the hint in the URL. Values are non-secret (clientId + authority), so no signing.

export interface SsoHintMicrosoft {
    clientId: string;
    authority: string;
}

export interface SsoHint {
    microsoft?: SsoHintMicrosoft;
}

// Query-string key used on the redirect to the id app.
export const SSO_HINT_PARAM = 'sso';

export class SsoHintUtility {

    // Encode the hint as a base64url string suitable for a URL query param. Returns null when there is
    // nothing to carry (standard tenants), so callers can skip adding the param entirely.
    public static encode(hint?: SsoHint | null): string | null {

        // All-or-nothing: only carry a fully-configured provider (clientId AND authority).
        if (!hint?.microsoft?.clientId || !hint.microsoft.authority) return null;

        const json = JSON.stringify(hint);
        return SsoHintUtility.toBase64Url(json);
    }

    // Decode a base64url hint param back into the structured hint. Returns null on any malformed input.
    public static decode(value?: string | null): SsoHint | null {

        if (!value) return null;

        try {
            const json = SsoHintUtility.fromBase64Url(value);
            const hint = JSON.parse(json) as SsoHint;
            return hint?.microsoft?.clientId && hint.microsoft.authority ? hint : null;
        } catch {
            return null;
        }
    }

    private static toBase64Url(value: string): string {

        const base64 = btoa(unescape(encodeURIComponent(value)));
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }

    private static fromBase64Url(value: string): string {

        const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
        return decodeURIComponent(escape(atob(base64)));
    }
}
